/**
 * API client for the live call co-pilot (`crm-calls/pilot.routes.ts`).
 *
 * Its own file rather than more of `calls.ts`: that one is already the settings,
 * the dialer, the history, the recordings and the caller ids, and this is a
 * different conversation with the server - one that happens while a call is
 * running, in windows, on a deadline.
 *
 * The window upload does not go through `api.ts`: that wrapper sets a JSON
 * content type and stringifies the body, and this posts multipart form data.
 * Everything else about it is the same - same origin, same session cookie.
 */
import { ApiError, api } from "./api";
import type { PilotBlocker } from "./calls";

/**
 * Who said a line, or "unknown" when the audio did not say.
 *
 * The third value is not an error state. The live listener that reads a single
 * downmixed clip infers the speaker from what a sentence sounds like, and when
 * it cannot, it says so rather than guessing - a wrong name changes who said
 * what, an absent one is only less informative. The row carries the API's string
 * straight through, so anything drawing a turn must have a branch for it.
 */
export type PilotSpeaker = "operator" | "client" | "unknown";
export type PilotFactSource = "scanner" | "register" | "history" | "mail" | "brain";

export interface PilotTurn {
  id?: string;
  seq: number;
  speaker: PilotSpeaker;
  text: string;
  startedAt?: string;
}

export interface PilotFact {
  source: PilotFactSource;
  label: string;
  value: string;
}

export interface PilotOpenQuestion {
  id: string;
  question: string;
  why: string;
}

/**
 * A handbook guide the co-pilot wants open, and why this call needs it.
 *
 * The server only ever sends a slug it checked against our own `KbArticle`
 * rows, and the title beside it is that row's - never the model's wording. So
 * `/guides/<slug>` is always a page that exists, which is what makes it safe to
 * put a link in front of somebody who is on the phone.
 */
export interface PilotGuideHint {
  slug: string;
  title: string;
  why: string;
}

export interface PilotState {
  answered: string[];
  open: PilotOpenQuestion[];
  /** Labels of the facts worth having on screen right now. */
  highlights: string[];
  nextSteps: string[];
  /** Handbook guides this conversation is about. More than one is normal. */
  guides: PilotGuideHint[];
  suggestedAtSeq: number;
}

export interface PilotChecklistItem {
  id: string;
  question: string;
  why: string;
  weight: number;
}

/** What the panel loads before the first window - and before the call is even answered. */
export interface PilotSnapshot {
  /** Switch on, a Gemini key configured AND a notice that plays to the callee. */
  ready: boolean;
  /**
   * Which piece is missing, so the panel can name the fix rather than the
   * symptom. Imported rather than written out again: this file used to hold its
   * own copy of the union, unconnected to the settings one, and a value added on
   * the server only ever reached whichever of the two somebody remembered.
   */
  blocker: PilotBlocker;
  windowSec: number;
  windows: number;
  maxWindows: number;
  turns: PilotTurn[];
  state: PilotState;
  facts: PilotFact[];
  hadContact: boolean;
  /** A site scan exists but could not name the platform with enough certainty. */
  scanUncertain: boolean;
  checklist: PilotChecklistItem[];
}

/** The answer to one uploaded window. `ok: false` carries a reason, not a failure. */
export interface PilotWindowResult {
  ok: boolean;
  error?:
    | "disabled"
    | "no_notice"
    | "budget_spent"
    | "quota_spent"
    | "too_large"
    | "model_failed"
    | "on_hold"
    /** Somebody stopped this call's recording, so nothing more of it is heard. */
    | "recording_stopped";
  detail?: string;
  turns: PilotTurn[];
  state: PilotState;
  facts: PilotFact[];
  windows: number;
  maxWindows: number;
}

export function getPilotSnapshot(callLogId: string, sinceSeq = -1): Promise<PilotSnapshot> {
  return api.get(`/admin/crm/calls/${encodeURIComponent(callLogId)}/live?sinceSeq=${sinceSeq}`);
}

/** The call is over; the server drops the context it cached for it. Best effort. */
export function endPilotSession(callLogId: string): Promise<{ ok: boolean }> {
  return api.post(`/admin/crm/calls/${encodeURIComponent(callLogId)}/live/end`, {});
}

export async function uploadPilotWindow(
  callLogId: string,
  window: { seq: number; startedAt: number; blob: Blob },
  signal?: AbortSignal,
  /**
   * Windows binned since the last one got through, because the model was slower
   * than the call.
   *
   * Carried on the next window that succeeds rather than reported on its own: a
   * dropped window never becomes a request, so this is the only messenger it
   * has, and a browser that is falling behind is the last one that should be
   * making an extra call to say so.
   */
  dropped = 0,
): Promise<PilotWindowResult> {
  const form = new FormData();
  // The audio part goes LAST: `@fastify/multipart` streams the parts in order,
  // and the route reads the scalar fields off the file part, which means they
  // have to have arrived by then.
  form.append("seq", String(window.seq));
  form.append("startedAt", String(window.startedAt));
  if (dropped > 0) form.append("dropped", String(dropped));
  form.append("audio", window.blob, `window-${window.seq}.wav`);

  const res = await fetch(`/api/admin/crm/calls/${encodeURIComponent(callLogId)}/live-window`, {
    method: "POST",
    body: form,
    credentials: "include",
    signal,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, data);
  return data as PilotWindowResult;
}

export function getPilotChecklist(): Promise<{ items: PilotChecklistItem[] }> {
  return api.get("/admin/crm/calls/pilot/checklist");
}

export function savePilotChecklist(
  items: Array<Partial<PilotChecklistItem> & { question: string }>,
): Promise<{ items: PilotChecklistItem[] }> {
  return api.put("/admin/crm/calls/pilot/checklist", { items });
}
